import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { range, get, isNil } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  getDealerFlow,
  ControlPanelSteps,
} from '@upstart/harmony/lib/flow-utils';
import {
  Condition,
  IncentivesProvider,
  CLIENT_TYPE,
  StoreStep,
  OnlineStep,
  Origin,
} from '@upstart/harmony/lib/constants';
import ErrorMessage from '../ErrorMessage';
import { DEALER_FEATURES, protectionPlanSource } from '../../util/constants';
import { isDepositEnabled } from '../../util/paymentUtils';
import './DealerStatusList.scss';
import LoadingOverlay from '../LoadingOverlay';

const FEATURE_COLUMN_COUNT = 9;
const FEATURE_METRIC_PREFIX = 'feature-metric-';

const EnableType = {
  Enabled: 'enabled',
  PartiallyEnabled: 'partiallyEnabled',
  Disabled: 'disabled',
};

const hasProtectionPlansEnabled = (dealer, clientType, stepId) => {
  if (get(dealer, 'protectionPlanSource') === protectionPlanSource.PEN) {
    const hasProducts = (get(dealer, 'penProducts') || []).length > 0;
    const hasStep =
      getDealerFlow(dealer)[clientType].findIndex(s => s.id === stepId) !== 1;
    return hasProducts && hasStep ? EnableType.Enabled : EnableType.Disabled;
  }
  return EnableType.Disabled;
};

const DEALER_FEATURE_PROPS = {
  [DEALER_FEATURES.Desking]: {
    name: 'Desking',
    metricKey: 'dealsWithPencil',
    metricDescription: 'Number of deals with a pencil',
    isEnabled: dealer =>
      get(dealer, 'desking.enabled') ? EnableType.Enabled : EnableType.Disabled,
  },
  [DEALER_FEATURES.SoftCreditPullsInstore]: {
    name: 'S Pulls In-Store',
    metricKey: null, // currently no metrics for this
    metricDescription: 'In-store soft credit pull is not implemented currently',
    isEnabled: dealer =>
      get(dealer, 'featureConfig.softCreditPull.inStore')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.SoftCreditPullsOnline]: {
    name: 'S Pulls Online',
    metricKey: 'dealsWithCreditPulls',
    metricDescription: 'Number of deals with a credit pull',
    isEnabled: dealer =>
      get(dealer, 'featureConfig.softCreditPull.online')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.DriverLicenseScan]: {
    name: 'DL Scan',
    metricKey: 'dealsWithDriversLicense',
    metricDescription: 'Number of deals with a scanned drivers license',
    isEnabled: dealer =>
      getDealerFlow(dealer)[CLIENT_TYPE.IN_STORE].findIndex(
        step =>
          step.id === ControlPanelSteps[CLIENT_TYPE.IN_STORE].DRIVER_LICENSE.id,
      ) !== -1
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.ThirdPartyLeadEnhancement]: {
    name: 'Third Party Lead Enhancement',
    metricKey: 'dealsCreatedAnysource',
    metricDescription: 'Number of deals created through any source',
    isEnabled: dealer =>
      get(dealer, 'featureConfig.thirdPartyLeads.forwardEmailToCustomer') ||
      get(dealer, 'featureConfig.thirdPartyLeads.forwardTextToCustomer')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.Online]: {
    name: 'Online',
    metricKey: 'dealsCreatedOnline',
    metricDescription: 'Number of deals created online',
    // Online is always enabled, even if the dealer isn't using it
    isEnabled: () => EnableType.Enabled,
  },
  [DEALER_FEATURES.InStore]: {
    name: 'In-Store',
    metricKey: 'dealsCreatedInstore',
    metricDescription: 'Number of deals created instore',
    // In-Store is always enabled, even if the dealer isn't using it
    isEnabled: () => EnableType.Enabled,
  },
  [DEALER_FEATURES.GlobalPricing]: {
    name: 'Global Pricing',
    metricKey: null, // currently no metrics for this
    metricDescription: 'Global pricing does not have metrics',
    isEnabled: dealer => {
      const usePriceOverrides =
        get(dealer, 'featureConfig.globalPricing.usePriceOverrides') || {};
      let enabledCount = 0;
      const sources = Object.values(Origin);
      sources.forEach(source => {
        if (usePriceOverrides[source]) enabledCount += 1;
      });
      if (enabledCount === 0) {
        return EnableType.Disabled;
      }
      if (enabledCount === sources.length) {
        return EnableType.Enabled;
      }
      return EnableType.PartiallyEnabled;
    },
  },
  [DEALER_FEATURES.TextDeal]: {
    name: 'Text Deal',
    metricKey: 'dealsSentToCustomer',
    metricDescription:
      'Number of deals that have been forwarded via text by dealers to customers',
    // Text Deal is always enabled, even if the dealer isn't using it
    isEnabled: () => EnableType.Enabled,
  },
  [DEALER_FEATURES.HotLeads]: {
    name: 'Hot Leads',
    metricKey: 'dealsSentInHotleads',
    metricDescription: 'Number of deals sent in hot leads emails',
    isEnabled: dealer =>
      get(dealer, 'wizard.hotLeads.enabled')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.ChromeData]: {
    name: 'ChromeData Incentives',
    metricKey: null,
    metricDescription: 'Incentives do not have metrics',
    isEnabled: dealer =>
      get(
        dealer,
        'featureConfig.incentivesProvider',
        IncentivesProvider.CHROMEDATA,
      ) === IncentivesProvider.CHROMEDATA
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.AIS]: {
    name: 'AIS Incentives',
    metricKey: null,
    metricDescription: 'Incentives do not have metrics',
    isEnabled: dealer =>
      get(dealer, 'featureConfig.incentivesProvider') === IncentivesProvider.AIS
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.VirtualShowroom]: {
    name: 'Virtual Showroom',
    metricKey: 'dealsWithChatUsed',
    metricDescription: 'Number of deals that have used Virtual Showroom',
    isEnabled: dealer =>
      get(dealer, 'featureConfig.virtualShowroom.enabled')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
  [DEALER_FEATURES.Deposit]: {
    name: 'Deposits',
    metricKey: 'dealsWithDeposit',
    metricDescription: 'Number of deals that submitted deposit',
    isEnabled: dealer =>
      isDepositEnabled(dealer) ? EnableType.Enabled : EnableType.Disabled,
  },
  [DEALER_FEATURES.ProtectionPlansOnline]: {
    name: 'Protection Plans Online',
    metricKey: 'dealsWithProductsOnline',
    metricDescription:
      'Number of deals created online that selected protection plans',
    isEnabled: dealer =>
      hasProtectionPlansEnabled(
        dealer,
        CLIENT_TYPE.ONLINE,
        OnlineStep.upgradePackages,
      ),
  },
  [DEALER_FEATURES.ProtectionPlansInstore]: {
    name: 'Protection Plans In-store',
    metricKey: 'dealsWithProductsInstore',
    metricDescription:
      'Number of deals created in-store that selected protection plans',
    isEnabled: dealer =>
      hasProtectionPlansEnabled(
        dealer,
        CLIENT_TYPE.IN_STORE,
        StoreStep.protectionPlans,
      ),
  },
  [DEALER_FEATURES.AgedUnits]: {
    name: 'Aged Units',
    metricKey: null,
    metricDescription: 'Aged units do not have metrics',
    isEnabled: dealer => {
      const daysUntilAgedUnit =
        get(dealer, 'tabletFormsConfig.vehicleSelection.daysUntilAgedUnit') ||
        {};
      return daysUntilAgedUnit[Condition.NEW] ||
        daysUntilAgedUnit[Condition.USED]
        ? EnableType.Enabled
        : EnableType.Disabled;
    },
  },
  [DEALER_FEATURES.StickerDeal]: {
    name: 'Send Sticker Deal',
    metricKey: 'dealsStickerSentToCustomer',
    metricDescription: 'Number of deals that have been sent as a sticker',
    isEnabled: dealer =>
      get(dealer, 'desking.enabled') ? EnableType.Enabled : EnableType.Disabled,
  },
  [DEALER_FEATURES.InsuranceApplications]: {
    name: 'Insurance Applications',
    metricKey: 'dealsWithInsuranceApplication',
    metricDescription: 'Number of deals that sent insurance application',
    isEnabled: dealer =>
      get(dealer, 'insurance.enabled')
        ? EnableType.Enabled
        : EnableType.Disabled,
  },
};

function getFeatureMetric(dealer, dealerMetrics, feature) {
  const { metricKey } = DEALER_FEATURE_PROPS[feature];
  if (metricKey) {
    const metric = dealerMetrics[metricKey];
    if (!isNil(metric)) {
      return metric;
    }
  }
  return 'N/A';
}

function DealerStatusList(props) {
  const {
    dealers,
    featureList,
    featureMetrics,
    onFeatureChange,
    expandedDealers,
    onDealerExpand,
    fetching,
  } = props;

  const tdStyles = { whiteSpace: 'normal', wordBreak: 'break-word' };

  const [sortProps, setSortProps] = useState({});
  const tableRef = useRef();

  const renderFeatureMetric = (dealer, feature) => {
    const dealerMetrics = (featureMetrics || {})[dealer.id];
    if (!dealerMetrics) {
      if (fetching) {
        return 'Loading...';
      }
      return (
        <span className="feature-metric-loading-error">Failed to Load</span>
      );
    }
    return (
      <div className="feature-metric">
        {getFeatureMetric(dealer, dealerMetrics, feature)}
        <OverlayTrigger
          placement="bottom"
          delayHide={300}
          overlay={
            <Tooltip id={`feature-metric-tooltip-${feature}-${dealer.id}`}>
              {DEALER_FEATURE_PROPS[feature].metricDescription}
            </Tooltip>
          }
        >
          <div className="feature-metric-description">?</div>
        </OverlayTrigger>
      </div>
    );
  };

  const renderFeatureContent = (cell, dealer, feature) => {
    const enabled = DEALER_FEATURE_PROPS[feature].isEnabled(dealer, feature);
    let indicateClass = '';
    if (enabled === EnableType.Enabled) {
      indicateClass = 'active';
    } else if (enabled === EnableType.PartiallyEnabled) {
      indicateClass = 'partial';
    }
    return (
      <div className="feature-value-container">
        <div className={`feature-indicate ${indicateClass}`} />
        {expandedDealers.includes(dealer.id) &&
          renderFeatureMetric(dealer, feature)}
      </div>
    );
  };

  const renderFeatureHeader = featureIndex => {
    const onFeatureSelect = (index, value) => {
      // if the currently changing feature was selected for the sort, clear the sort
      if (sortProps.name === featureList[featureIndex]) {
        tableRef.current.cleanSort();
      }
      onFeatureChange(index, value);
    };
    return (
      <TableHeaderColumn
        dataSort
        dataField={FEATURE_METRIC_PREFIX + featureList[featureIndex]}
        key={featureIndex}
        formatExtraData={featureList[featureIndex]}
        dataFormat={renderFeatureContent}
      >
        <select
          id={`feature-select-${featureIndex}`}
          className="feature-select"
          value={featureList[featureIndex]}
          onClick={event => event.stopPropagation()}
          onChange={event => onFeatureSelect(featureIndex, event.target.value)}
        >
          {Object.values(DEALER_FEATURES)
            .filter(feature => DEALER_FEATURE_PROPS[feature])
            .map(feature => (
              <option
                value={feature}
                key={feature}
                id={`feature-select-${featureIndex}-option-${feature}`}
              >
                {DEALER_FEATURE_PROPS[feature].name}
              </option>
            ))}
        </select>
      </TableHeaderColumn>
    );
  };

  // use a proxy to let the table read feature enabled values for sorting
  const proxiedDealerList = dealers.map(
    dealer =>
      new Proxy(dealer, {
        get(obj, prop) {
          if (prop.startsWith(FEATURE_METRIC_PREFIX)) {
            const metricProp = prop.substring(FEATURE_METRIC_PREFIX.length);
            if (Object.values(DEALER_FEATURES).includes(metricProp)) {
              return DEALER_FEATURE_PROPS[metricProp].isEnabled(dealer);
            }
          }
          return obj[prop];
        },
      }),
  );

  const tableOptions = {
    onRowClick: dealer => onDealerExpand(dealer.id),
    onSortChange: (name, order) => setSortProps({ name, order }),
  };

  return (
    <LoadingOverlay active={fetching}>
      {props.error && <ErrorMessage error={props.error} />}
      <div>
        <BootstrapTable
          ref={tableRef}
          data={proxiedDealerList}
          striped
          hover
          pagination
          options={tableOptions}
          bodyContainerClass="list-body-container"
          tableStyle={{ border: 'none' }}
          trClassName="nav-hover"
        >
          <TableHeaderColumn isKey dataSort dataField="name" tdStyle={tdStyles}>
            Dealer Name
          </TableHeaderColumn>
          {range(FEATURE_COLUMN_COUNT).map(index => renderFeatureHeader(index))}
        </BootstrapTable>
      </div>
    </LoadingOverlay>
  );
}

DealerStatusList.propTypes = {
  dealers: PropTypes.array,
  featureList: PropTypes.array,
  featureMetrics: PropTypes.object,
  onFeatureChange: PropTypes.func,
  expandedDealers: PropTypes.array,
  onDealerExpand: PropTypes.func,
  error: PropTypes.any,
  fetching: PropTypes.bool,
};

export default DealerStatusList;
